import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { RouteParamService } from '../shared/service/route-param.service';
import { SearchResult } from '../api/models/search-result';
import { SiteSearchApiService } from '../api/services/site-search-api.service';
import { SearchUrlParameters } from '../shared/model/search-url-parameters';
import { SearchParams } from '../api/models/search-params';
import { SearchEventPublisher } from '../shared/service/search-event-publisher.service';
import { SearchEventTypeEnum } from '../shared/model/search-event-type-enum';
import { PartialResultFieldEnum } from '../api/models/partial-result-field-enum';
import { AnalyticsService } from '../shared/service/analytics/analytics.service';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { SearchPageEvent } from '../shared/model/search-page-event';
import { SignalService } from '../shared/service/signal/signal.service';
import { CATEGORY_FACET_IN_URL } from '../shared/ni-constants';
import { CookieService } from 'ngx-cookie-service';
import { activate, close, fade, hide, setTabIndex } from './search-main-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, takeUntil } from 'rxjs';
import { FacetDataHolderService } from '../shared/service/facet-data-holder.service';

declare var NI: any;
const wrapperFormId = 'wrapper-search-form';

@Component({
  selector: 'app-search-main',
  templateUrl: './search-main.component.html',
  styleUrls: ['./search-main.component.scss'],
})
@UntilDestroy()
export class SearchMainComponent implements OnInit, AfterViewInit, OnDestroy {
  searchResult?: SearchResult;
  searchParams: SearchUrlParameters = {};
  pageShortTitle = '';
  lastEvent?: SearchPageEvent;
  initialized = false;
  unsubscribe = new Subject<void>();
  removeListener?: () => void;

  constructor(
    private routeParamService: RouteParamService,
    private renderer: Renderer2,
    private searchApi: SiteSearchApiService,
    private searchEventPublisher: SearchEventPublisher,
    private translateService: TranslateService,
    private titleService: Title,
    private analyticsService: AnalyticsService,
    private signalService: SignalService,
    private router: Router,
    private cookieService: CookieService,
    private facetDataHolder: FacetDataHolderService
  ) {}

  ngOnInit(): void {
    this.openSearchBoxInHeader();
    this.translateService
      .get('searchResults')
      .pipe(untilDestroyed(this))
      .subscribe(title => {
        this.pageShortTitle = title;
        this.updateTitle();
      });
    this.facetDataHolder.loadFacetDataIfNeeded();
    this.setupSubscriptions();
  }

  ngAfterViewInit(): void {
    this.removeListener = this.renderer.listen('window', 'triggerNewSearch', (e: CustomEvent<SearchParams>) => {
      document.querySelector('#niSearchResults')?.classList.remove('open');
      const searchBoxParams = {
        ...this.searchParams,
        pg: 1,
        q: e.detail.q,
      };
      if (e.detail.sn && e.detail.sn != '') {
        searchBoxParams.sn = e.detail.sn;
      }
      this.lastEvent = {
        eventType: SearchEventTypeEnum.SEARCH_FROM_HEADER,
        searchParams: searchBoxParams,
      };
      this.router.navigate([], { queryParams: searchBoxParams }).then();
    });
  }

  ngOnDestroy(): void {
    if (this.removeListener) {
      this.removeListener();
    }
    this.unsubscribe.complete();
  }

  private setupSubscriptions() {
    this.routeParamService
      .getSearchStateFromRouteParam()
      .pipe(untilDestroyed(this))
      .subscribe(params => this.handleRouteParamChange(params));

    this.searchEventPublisher
      .getSearchTrigger()
      .pipe(untilDestroyed(this))
      .subscribe(searchEvent => {
        this.lastEvent = searchEvent;

        switch (searchEvent.eventType) {
          case SearchEventTypeEnum.CHANGE_PAGE:
            this.router.navigate([], { queryParams: searchEvent.searchParams }).then();
            break;

          case SearchEventTypeEnum.SEARCH_FROM_DYM_REFRESH:
            this.doSearch(SearchEventTypeEnum.SEARCH_FROM_DYM_REFRESH);
            break;

          default: {
            const queryParams = { ...searchEvent.searchParams };
            if (this.searchParams.ps) {
              queryParams.ps = this.searchParams.ps;
            }
            this.router.navigate([], { queryParams }).then();
            break;
          }
        }
      });
  }

  private handleRouteParamChange(params: SearchUrlParameters) {
    const oldParams = this.searchParams;
    this.searchParams = params;
    this.updateSearchBoxInput();
    if (this.lastEvent && this.searchResult) {
      if (this.searchParams.q !== oldParams.q) {
        if (this.lastEvent.eventType === SearchEventTypeEnum.SEARCH_FROM_HEADER) {
          const queryParams = {
            ...this.searchParams,
            fil: undefined,
          };
          this.router.navigate([], { queryParams }).then();
        }
      }

      this.doSearch(this.lastEvent.eventType);
      this.lastEvent = undefined;
    } else {
      this.doSearch(SearchEventTypeEnum.INITIAL_SEARCH);
    }
  }

  private getAdditionalQueryParams(): SearchParams {
    let ret: SearchParams = {};

    if ('true' === this.cookieService.get('search_testing')) {
      ret.searchTesting = 'true';
    }

    let disableAutoCorrect: string = this.cookieService.get('disable_auto_correct') ?? '';
    if (disableAutoCorrect.length > 0) {
      ret.disableAutoCorrect = this.cookieService.get('disable_auto_correct');
    }

    let locale: string = this.cookieService.get('locale') ?? '';
    let currentLang: string = this.translateService.currentLang ?? '';
    if (locale.length > 0) {
      ret.locale = locale;
    } else if (currentLang.length > 0) {
      ret.locale = this.translateService.currentLang;
    }

    ret.firstLoad = !this.initialized;

    return ret;
  }

  private doSearch(searchEvent: SearchEventTypeEnum) {
    this.unsubscribe.next();

    let queryParams: SearchParams = {
      ...this.searchParams,
      ...this.getAdditionalQueryParams(),
    };

    let excludeFields: PartialResultFieldEnum[] = [];
    switch (searchEvent) {
      case SearchEventTypeEnum.CHANGE_PAGE:
      case SearchEventTypeEnum.SORT_RESULT:
        excludeFields = [PartialResultFieldEnum.CategoryFacet, PartialResultFieldEnum.NarrowByFacets];
        break;
    }

    if (excludeFields?.length) {
      queryParams.excludeFields = excludeFields;
    }

    this.searchApi
      .getResult(queryParams)
      .pipe(untilDestroyed(this))
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: result => {
          if (excludeFields?.length) {
            const preservedFields: any = {};
            excludeFields.forEach(field => (preservedFields[field] = this.searchResult?.[field]));
            this.searchResult = { ...preservedFields, ...result };
          } else {
            this.searchResult = result;
          }
          this.printSearchServiceUriIfNeeded(result);
        },
        error: err => {
          console.log(`Server responded with error: ${err.message}`);
          this.searchResult = {
            searchResultList: [],
            pagination: { totalResults: 0 },
          };
          this.initialized = true;
        },
        complete: () => {
          if (this.searchResult) {
            this.updateTitle();
            this.analyticsService.trackVirtualPageLoad(this.initialized, this.pageShortTitle, this.searchResult);
          }
          this.signalService.lastSearchParams = this.searchParams;
          this.signalService.lastSearchResult = this.searchResult;
          this.updateSearchBoxForm();
          this.initialized = true;
        },
      });
  }

  private printSearchServiceUriIfNeeded(result: SearchResult) {
    this.printDetails('Primary Search Service URI', result.searchServiceUri);
    this.printDetails('Secondary Search Service URI', result.secondarySearchServiceUri);
  }

  private printDetails(title: string, uri?: string) {
    if (!uri?.length) {
      return;
    }

    console.group(title);
    console.info(uri);

    let data = uri;

    try {
      data = 'Search Service Params:';
      let url: URL = new URL(uri);
      url.searchParams.forEach((value, key) => {
        data += '\n\t- ' + key + ': ' + value;
      });
      console.info(data);
    } catch (e) {
      // swallow
    }
    console.groupEnd();
  }

  private updateTitle() {
    if (this.searchResult?.searchDym?.dymActive) {
      this.titleService.setTitle(`${this.pageShortTitle}: ${this.searchResult?.searchDym?.originalTerm} - NI`);
    } else {
      this.titleService.setTitle(`${this.pageShortTitle} - NI`);
    }
  }

  private updateSearchBoxInput() {
    if (this.searchParams.q) {
      const input = document.querySelector<HTMLInputElement>(`#${wrapperFormId} .niAutocompleteInput`);
      if (input) {
        input.value = this.searchParams.q;
      }
    }
  }

  private updateSearchBoxForm() {
    const autoCompleteScenario = this.searchResult?.lastSelectedCategoryFacetCode ?? 'global';
    const selectedCategoryFacetValue = this.searchResult?.selectedCategoryFacetCodes
      ? `${CATEGORY_FACET_IN_URL}:${this.searchResult?.selectedCategoryFacetCodes?.join('.')}`
      : '';
    const inputClass = 'searchFilterDropDown';
    const wrapperForm = document.querySelector('#' + wrapperFormId);
    let hiddenInput = wrapperForm?.querySelector(`.${inputClass}`);
    if (!hiddenInput) {
      hiddenInput = document.createElement('input');
      hiddenInput.setAttribute('type', 'hidden');
      hiddenInput.classList.add('searchFilterDropDown');
      wrapperForm?.appendChild(hiddenInput);
    }
    hiddenInput.setAttribute('data-scenario', autoCompleteScenario);
    hiddenInput.setAttribute('data-initialfacet', selectedCategoryFacetValue);
    if (typeof NI !== 'undefined' && NI.pnx) {
      NI.pnx.autocomplete.initialize();
    }
  }

  private closeMobileNav() {
    let mobileNav = document.querySelector('.mobile-nav');
    let mobileSpacer = document.querySelector('.mobile-spacer');
    let headerMenu = document.querySelector('.header-menu');
    let niLogo = document.querySelector('.ni-logo');
    let toggle = document.querySelector('.toggle');

    hide(mobileNav);
    close(mobileNav);
    document.body.classList.remove('menu-open', 'noscroll');
    [headerMenu, mobileSpacer, niLogo, toggle].forEach(el => {
      if (el instanceof HTMLElement) {
        el.classList.remove('active');
      }
    });
  }

  private openSearchBoxInHeader() {
    let account = document.querySelector('.nia-menu-item.account');
    let accountLinks = document.querySelectorAll('.nia-menu-item.account a');
    let closeButton = document.querySelector('.close');
    let menuItems = document.querySelectorAll('.nia-menu-item:not(.cart-badge)');
    let menuLinks = document.querySelectorAll('.nia-menu-item:not(.cart-badge) > a');
    let wrapperSearch = document.querySelector('.wrapper-search');
    let wrapperSearchForm = document.querySelector('.wrapper-search-form');

    hide(wrapperSearch);
    activate(closeButton);
    activate(wrapperSearchForm);
    hide(account);

    menuItems.forEach(el => fade(el));
    menuLinks.forEach(el => setTabIndex(el));
    accountLinks.forEach(el => setTabIndex(el));

    if (window.innerWidth < 1100) {
      this.closeMobileNav();
    }

    document.querySelectorAll('.header-menu > li:not(.wrapper-search-form)').forEach(item => {
      if (item instanceof HTMLElement) item.style.visibility = 'hidden';
    });

    document.querySelector('.niAutocompleteInput')?.setAttribute('data-search-type', 'filteredSearch');
    document.querySelector('.niAutocompleteInput')?.setAttribute('data-trigger', 'search-btn');
  }
}
